import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth, API } from '@/context/AuthContext';
import { FileText, ArrowRight, Loader2, Inbox, Users, ShieldCheck } from 'lucide-react';

const PortalDashboard = () => {
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [pagesRes, statsRes] = await Promise.all([
          axios.get(`${API}/pages`),
          user.role === 'admin' ? axios.get(`${API}/admin/stats`) : Promise.resolve({ data: null }),
        ]);
        setPages(pagesRes.data);
        setStats(statsRes.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.role]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          Hello, {user.name.split(' ')[0]} 👋
        </h1>
        <p className="text-slate-600 text-sm mt-1">
          Signed in as <b className="capitalize">{user.role}</b>. Below are the internal pages available to you.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Users} label="Total users" value={stats.total_users} tone="blue" />
          <StatCard icon={ShieldCheck} label="Active users" value={stats.active_users} tone="green" />
          <StatCard icon={Inbox} label="Pending approvals" value={stats.pending_users} tone="amber" />
          <StatCard icon={FileText} label="Published pages" value={stats.published_pages} tone="blue" />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-heading font-bold text-slate-900">Your pages</h2>
          <span className="text-xs text-slate-500">{pages.length} item(s)</span>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </div>
        ) : err ? (
          <div className="p-8 text-center text-red-600">{err}</div>
        ) : pages.length === 0 ? (
          <div className="p-10 text-center">
            <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-600 font-medium">No pages available yet</p>
            <p className="text-slate-500 text-sm mt-1">
              {user.role === 'admin' ? 'Create your first page from Admin → Pages.' : 'An admin has not yet assigned any pages to your role.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {pages.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/portal/pages/${p.slug}`}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg grid place-items-center bg-[#eaf3fb] text-[#1e73be] flex-none">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{p.title}</p>
                    <p className="text-sm text-slate-500 truncate">{p.excerpt || 'No description'}</p>
                  </div>
                  <div className="hidden md:flex flex-wrap gap-1 justify-end">
                    {(p.visible_roles || []).map((r) => (
                      <span key={r} className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                        {r}
                      </span>
                    ))}
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 flex-none" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, tone }) => {
  const tones = {
    blue: { bg: '#eaf3fb', fg: '#1e73be' },
    green: { bg: '#dcf7e7', fg: '#2ea690' },
    amber: { bg: '#fff6e0', fg: '#c88a1e' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg grid place-items-center flex-none" style={{ backgroundColor: t.bg, color: t.fg }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="font-heading font-bold text-xl text-slate-900 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
};

export default PortalDashboard;
